import type {
  AiAssistantFlowType,
  ProgressNoteContext,
  TreatmentPlanGuidedContext,
  TreatmentPlanRevisionContext,
} from './types';

export interface AiAssistantActiveSession {
  flow: Exclude<AiAssistantFlowType, 'general-chat'>;
  editorSessionKey: string;
  patientId: string;
  documentKey: string;
  launchNonce: string;
}

export interface AiAssistantState {
  open: boolean;
  flow: AiAssistantFlowType | null;
  revisionContext: TreatmentPlanRevisionContext | null;
  guidedContext: TreatmentPlanGuidedContext | null;
  progressNoteContext: ProgressNoteContext | null;
  activeSession: AiAssistantActiveSession | null;
}

export type AiAssistantAction =
  | { type: 'OPEN_DRAWER'; flow?: AiAssistantFlowType }
  | { type: 'CLOSE_DRAWER' }
  | { type: 'TOGGLE_DRAWER' }
  | { type: 'REGISTER_REVISION'; context: TreatmentPlanRevisionContext | null }
  | { type: 'REGISTER_GUIDED'; context: TreatmentPlanGuidedContext | null }
  | { type: 'REGISTER_PROGRESS_NOTE'; context: ProgressNoteContext | null }
  | { type: 'START_QUICK'; context: TreatmentPlanGuidedContext }
  | { type: 'START_GUIDED'; context: TreatmentPlanGuidedContext }
  | { type: 'START_REVISION' }
  | { type: 'START_PROGRESS_NOTE' }
  | { type: 'OPEN_GENERAL_CHAT' }
  | { type: 'PROMOTE_DOCUMENT_KEY'; documentKey: string; editorSessionKey: string }
  | {
      type: 'NAVIGATE';
      pathPatientId: string | null;
      pathDocumentIdentity: string | null;
      pathname: string;
    }
  | { type: 'CLEAR_ALL' };

const newLaunchNonce = (): string =>
  `launch-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

function isCreateFlow(flow: AiAssistantFlowType | null | undefined): boolean {
  return flow === 'treatment-plan-quick-generate' || flow === 'treatment-plan-guided-create';
}

function isProgressNoteFlow(flow: AiAssistantFlowType | null | undefined): boolean {
  return flow === 'progress-note-generate';
}

function sameEditorSession(
  context: { editorSessionKey?: string; patientId: string; documentKey: string } | null,
  session: AiAssistantActiveSession | null,
): boolean {
  if (!context || !session || context.patientId !== session.patientId) return false;
  if (context.editorSessionKey && session.editorSessionKey) {
    return context.editorSessionKey === session.editorSessionKey;
  }
  return context.documentKey === session.documentKey;
}

function mergeGuidedClinicalFields(
  active: TreatmentPlanGuidedContext,
  incoming: TreatmentPlanGuidedContext,
): TreatmentPlanGuidedContext {
  return {
    ...active,
    mode: active.mode,
    documentKey: incoming.documentKey || active.documentKey,
    editorSessionKey: incoming.editorSessionKey ?? active.editorSessionKey,
    episodeId: incoming.episodeId || active.episodeId,
    questionnaireId: incoming.questionnaireId || active.questionnaireId,
    geminiAiDisabled: incoming.geminiAiDisabled,
    onPlanGenerated: incoming.onPlanGenerated,
    diagnoses: incoming.diagnoses,
    symptoms: incoming.symptoms,
    operation: incoming.operation,
    treatmentFrequency: incoming.treatmentFrequency,
  };
}

/** Stable clinical fingerprint — skip provider re-renders when form text edits don't change AI context. */
export function guidedClinicalFingerprint(
  context: Pick<
    TreatmentPlanGuidedContext,
    | 'documentKey'
    | 'editorSessionKey'
    | 'episodeId'
    | 'questionnaireId'
    | 'geminiAiDisabled'
    | 'diagnoses'
    | 'symptoms'
    | 'operation'
    | 'treatmentFrequency'
    | 'mode'
  > | null | undefined,
): string {
  if (!context) return '';
  return JSON.stringify({
    documentKey: context.documentKey,
    editorSessionKey: context.editorSessionKey ?? '',
    episodeId: context.episodeId,
    questionnaireId: context.questionnaireId,
    geminiAiDisabled: Boolean(context.geminiAiDisabled),
    diagnoses: (context.diagnoses ?? []).map((d) => `${d.code}\0${d.display}`),
    symptoms: (context.symptoms ?? []).map((s) => s.label),
    operation: context.operation ?? '',
    treatmentFrequency: context.treatmentFrequency ?? '',
    mode: context.mode ?? '',
  });
}

function startCreateFlow(
  state: AiAssistantState,
  flow: 'treatment-plan-quick-generate' | 'treatment-plan-guided-create',
  context: TreatmentPlanGuidedContext,
): AiAssistantState {
  const mode = flow === 'treatment-plan-quick-generate' ? 'quick' : 'guided';
  return {
    ...state,
    open: true,
    flow,
    guidedContext: { ...context, kind: 'treatment-plan-guided-create', mode },
    activeSession: {
      flow,
      editorSessionKey: context.editorSessionKey ?? `${context.patientId}:${context.documentKey}`,
      patientId: context.patientId,
      documentKey: context.documentKey,
      launchNonce: newLaunchNonce(),
    },
  };
}

function promoteDocumentKey<T extends { editorSessionKey?: string; documentKey: string }>(
  ctx: T | null,
  documentKey: string,
  editorSessionKey: string,
): T | null {
  if (!ctx) return null;
  if (ctx.editorSessionKey && ctx.editorSessionKey !== editorSessionKey) return ctx;
  if (ctx.documentKey !== 'new' && ctx.documentKey !== documentKey) return ctx;
  return { ...ctx, documentKey };
}

function clearRevision(state: AiAssistantState): AiAssistantState {
  return {
    ...state,
    revisionContext: null,
    flow: state.flow === 'treatment-plan-revision' ? null : state.flow,
    activeSession:
      state.activeSession?.flow === 'treatment-plan-revision' ? null : state.activeSession,
  };
}

function clearCreate(state: AiAssistantState): AiAssistantState {
  return {
    ...state,
    guidedContext: null,
    flow: isCreateFlow(state.flow) ? null : state.flow,
    activeSession: isCreateFlow(state.activeSession?.flow) ? null : state.activeSession,
  };
}

function clearProgressNote(state: AiAssistantState): AiAssistantState {
  return {
    ...state,
    progressNoteContext: null,
    flow: isProgressNoteFlow(state.flow) ? null : state.flow,
    activeSession: isProgressNoteFlow(state.activeSession?.flow) ? null : state.activeSession,
  };
}

export function createInitialAiAssistantState(): AiAssistantState {
  return {
    open: false,
    flow: null,
    revisionContext: null,
    guidedContext: null,
    progressNoteContext: null,
    activeSession: null,
  };
}

export function aiAssistantReducer(
  state: AiAssistantState,
  action: AiAssistantAction,
): AiAssistantState {
  switch (action.type) {
    case 'OPEN_DRAWER':
      return { ...state, open: true, flow: action.flow ?? state.flow ?? 'general-chat' };

    case 'CLOSE_DRAWER':
      return { ...state, open: false };

    case 'TOGGLE_DRAWER':
      return { ...state, open: !state.open };

    case 'REGISTER_REVISION':
      if (!action.context) {
        return state.activeSession?.flow === 'treatment-plan-revision'
          ? state
          : { ...state, revisionContext: null };
      }
      return { ...state, revisionContext: action.context };

    case 'REGISTER_GUIDED': {
      const context = action.context;
      if (!context) {
        return isCreateFlow(state.activeSession?.flow) ? state : { ...state, guidedContext: null };
      }

      const session = state.activeSession;
      if (isCreateFlow(session?.flow) && sameEditorSession(context, session)) {
        const mode = session!.flow === 'treatment-plan-quick-generate' ? 'quick' : 'guided';
        const base = state.guidedContext ?? { ...context, mode };
        const merged = mergeGuidedClinicalFields(base, { ...context, mode });
        // Avoid re-rendering the drawer on every form keystroke when clinical context is unchanged.
        if (
          state.guidedContext
          && guidedClinicalFingerprint(state.guidedContext) === guidedClinicalFingerprint(merged)
          && state.activeSession?.documentKey === (context.documentKey || session!.documentKey)
          && state.activeSession?.editorSessionKey === (context.editorSessionKey ?? session!.editorSessionKey)
        ) {
          return state;
        }
        return {
          ...state,
          guidedContext: merged,
          activeSession: {
            ...session!,
            documentKey: context.documentKey || session!.documentKey,
            editorSessionKey: context.editorSessionKey ?? session!.editorSessionKey,
          },
        };
      }
      if (isCreateFlow(session?.flow)) return state;

      if (
        state.guidedContext
        && guidedClinicalFingerprint(state.guidedContext) === guidedClinicalFingerprint({ ...context, mode: undefined })
      ) {
        return state;
      }
      return { ...state, guidedContext: { ...context, mode: undefined } };
    }

    case 'REGISTER_PROGRESS_NOTE':
      if (!action.context) {
        return isProgressNoteFlow(state.activeSession?.flow)
          ? state
          : { ...state, progressNoteContext: null };
      }
      return { ...state, progressNoteContext: action.context };

    case 'START_QUICK':
      return startCreateFlow(state, 'treatment-plan-quick-generate', action.context);

    case 'START_GUIDED':
      return startCreateFlow(state, 'treatment-plan-guided-create', action.context);

    case 'START_REVISION':
      if (!state.revisionContext) return state;
      return {
        ...state,
        open: true,
        flow: 'treatment-plan-revision',
        activeSession: {
          flow: 'treatment-plan-revision',
          editorSessionKey:
            state.revisionContext.editorSessionKey
            ?? `${state.revisionContext.patientId}:${state.revisionContext.documentKey}`,
          patientId: state.revisionContext.patientId,
          documentKey: state.revisionContext.documentKey,
          launchNonce: newLaunchNonce(),
        },
      };

    case 'START_PROGRESS_NOTE': {
      if (!state.progressNoteContext) return state;
      const ctx = state.progressNoteContext;
      const session = state.activeSession;
      // Reopening the same appointment keeps the existing launchNonce so the drawer
      // does not remount the flow — the provider continues one conversation per note.
      const continuesSession =
        session?.flow === 'progress-note-generate' && session.documentKey === ctx.documentKey;
      return {
        ...state,
        open: true,
        flow: 'progress-note-generate',
        activeSession: continuesSession
          ? session
          : {
            flow: 'progress-note-generate',
            editorSessionKey: ctx.documentKey,
            patientId: ctx.patientId ?? '',
            documentKey: ctx.documentKey,
            launchNonce: newLaunchNonce(),
          },
      };
    }

    case 'OPEN_GENERAL_CHAT':
      return { ...state, open: true, flow: 'general-chat', activeSession: null };

    case 'PROMOTE_DOCUMENT_KEY': {
      const { documentKey, editorSessionKey } = action;
      const session = state.activeSession;
      return {
        ...state,
        revisionContext: promoteDocumentKey(state.revisionContext, documentKey, editorSessionKey),
        guidedContext: promoteDocumentKey(state.guidedContext, documentKey, editorSessionKey),
        activeSession:
          session?.editorSessionKey === editorSessionKey
            ? { ...session, documentKey }
            : session,
      };
    }

    case 'NAVIGATE': {
      const { pathPatientId, pathDocumentIdentity, pathname } = action;
      let next = state;

      if (next.revisionContext) {
        const identity = `${next.revisionContext.patientId}:${next.revisionContext.documentKey}`;
        const samePatient = pathPatientId === next.revisionContext.patientId;
        const sameDoc = !pathDocumentIdentity || pathDocumentIdentity === identity;
        if (!samePatient || (pathDocumentIdentity && !sameDoc)) next = clearRevision(next);
      }

      const patientId = next.activeSession?.patientId ?? next.guidedContext?.patientId;
      const documentKey = next.activeSession?.documentKey ?? next.guidedContext?.documentKey;
      if (patientId && documentKey) {
        const identity = `${patientId}:${documentKey}`;
        const samePatient = pathPatientId === patientId;
        const sameDoc = !pathDocumentIdentity || pathDocumentIdentity === identity;
        if (!samePatient || (pathDocumentIdentity && !sameDoc)) next = clearCreate(next);
      }

      if (!pathname.includes('/dashboard/appointments/')) {
        next = clearProgressNote(next);
      }

      return next;
    }

    case 'CLEAR_ALL':
      return {
        ...state,
        revisionContext: null,
        guidedContext: null,
        progressNoteContext: null,
        flow: null,
        activeSession: null,
      };

    default:
      return state;
  }
}

export function isTreatmentPlanCreateFlow(
  flow: AiAssistantFlowType | null | undefined,
): flow is 'treatment-plan-quick-generate' | 'treatment-plan-guided-create' {
  return isCreateFlow(flow);
}
